// @mui
import { Theme } from '@mui/material/styles'
//
import { HINT_DISMISS_CLASS } from 'src/components/HintDismiss'
import Iconify from 'src/components/Iconify'
import { ALERT_TONES, AlertTone, TONE_INK, toneGlyph } from 'src/design-system'
import {
  colorTokens,
  icons as iconTokens,
  layout as layoutTokens,
  semanticTokens,
  typography as typeTokens,
} from '../tokens.generated'

// ----------------------------------------------------------------------
// Severity picks the tone, and blue means no action needed.
//
// The console had four tones for five meanings, so the fifth borrowed. *Your
// account does not have a subscription* (the sentence that explains why half
// the page below it is inert) rendered in the same blue as *This provider has
// no subscription, so there are no terms to set*, which genuinely is a
// footnote. One tone, two meanings, and the consequential one styled as the
// ignorable one.
//
//   info       A fact. Nothing is blocked and nothing is expected of you.
//   attention  Something is not set up and a capability is off until it is.
//   warning    Something will go wrong soon if it is left alone.
//   error      It already failed.
//
// `attention` is the orange one and it is the tone for every "you have not
// finished setting this up" state in the console: subscription, Stripe,
// Tesla, the setup items. Blue is the tone you may ignore, which is why an
// info alert is the one tone that may not carry an action: if there is
// something to do, it is not a footnote. That rule is held by
// `scripts/conformanceRules.mjs`, on the rendered page.
//
// MUI has four severities and takes a fifth without complaint: its built-in
// per-colour styles come from the palette entries that have both `main` and
// `light`, so a tone with no palette slot matches none of them and this file
// supplies all of it. The slot name `standardAttention` is what
// `overridesResolver` composes from the severity, so it lands.
//
// **A tint and the ink on it are one pair, and the pair is already per
// scheme.** This used to pick the slot by scheme (`lighter` in light,
// `darker` in dark), which reads as careful and is the bug: the semantic slots
// are built from tokens that invert on their own, so swapping them a second
// time cancels the inversion. In dark mode an info alert came out with a pale
// lavender fill carrying twenty percent alpha ink, which is not text. Light
// mode was correct, which is why it shipped.
//
// The pair comes off the semantic roles rather than MUI's legacy slots, which
// do not agree with each other about which end they hold: success puts its
// readable ink in `dark` while warning and error put theirs in `darker`.
//
// **The mark is stroked.** It was a filled disc with a white glyph knocked out
// of it: the one solid mark in a product whose every other glyph is a stroke,
// and the reason an alert read as a different family from the row it sat in.
// Which glyph belongs to which tone is in the token source, so a tone cannot
// pick up a second mark on another surface.
//
// **A hint is a box with one inset and a mark on the copy's centre line.** It
// was neither. MUI insets the root 6 / 16, pads its icon `7px 0` and its
// message `8px 0`, and this file used to zero the icon's *top* padding only,
// so the icon box was 2 + 20 + 7 tall inside a root padded 6, the copy sat
// under 6px of space and over 16, and the mark's centre came out 3px below
// the centre of the line it introduces. Every hint in the console drew that,
// which is why the fix is one inset here rather than an `sx` per screen.
//
// **The copy shares the mark's line; it does not start under it.** Every hint
// in the console drew its glyph alone on the first line with the sentence
// beginning on the second, and one declaration did it: the message slot took
// `width: 100%` while the root wraps. A flex item whose hypothetical size is
// the whole line cannot share that line with the mark beside it, so it folded,
// on a 900px card exactly as readily as on a phone, because the width was
// never the variable.
//
// The load-bearing part of the replacement is the **zero basis**. `flexGrow:
// 1` with `flexBasis: 0` gives the copy whatever the mark leaves and nothing
// it has not been given; `flexBasis: 'auto'` reads as the same sentence and is
// not, because at `auto` the basis is the copy's own content width, so the
// line folds again the moment the column is narrower than the sentence: every
// narrow hint in the product, and none of the wide ones a fix gets looked at
// in. `minWidth: 0` goes with it: a flex item will not shrink below its
// longest unbreakable token unless it is told it may, and half the copy here
// is an address.
//
// **With a title, the line the mark belongs to is the title's.** The icon box
// is one line of the copy, and a title is `body` where the copy is `small`, so
// on a hint that carries one the mark sat high of the sentence it introduces.
// `AlertTitle` also arrives with MUI's `-2px` top margin, which goes for the
// same reason the icon's nudge did just above: a nudge is a measurement of one
// rendering.
// ----------------------------------------------------------------------

/**
 * One line of the copy, in px.
 *
 * MUI gives an alert `theme.typography.body2`, which the scale carries as the
 * `small` role, so this is the box a single line of an alert occupies. The
 * mark is centred against it rather than against the message block: a block
 * may be three lines tall, and a mark on the middle of a paragraph belongs to
 * none of them.
 */
const COPY_LINE =
  typeTokens.console.small.size * typeTokens.console.small.lineHeight

/**
 * One line of the title, in px.
 *
 * `AlertTitle` renders as `body1`, which the scale carries as `body`, so a
 * hint that carries a title opens on a taller line than one that does not.
 * The mark is centred on the line it introduces, and with a title that is the
 * title's line rather than the first line of the sentence under it.
 */
const TITLE_LINE =
  typeTokens.console.body.size * typeTokens.console.body.lineHeight

/**
 * The dismiss control's box, and how far it overhangs the line it is centred
 * on.
 *
 * `HintDismiss` is `size="small"`, which `MuiIconButton` resolves to the small
 * control height. Stated from the same token rather than measured, so the
 * corner follows the control if the ramp moves.
 */
const DISMISS_BOX = layoutTokens.controlHeightSmall

const DISMISS_OVERHANG = (DISMISS_BOX - COPY_LINE) / 2

const DISMISS_OVERHANG_TITLE = (DISMISS_BOX - TITLE_LINE) / 2

export default function Alert(theme: Theme) {
  const roles = (tone: AlertTone) => theme.palette.semantic[tone]

  /**
   * A button on a tint is one thing everywhere: the card's fill, and the
   * tone's ink for its label and its edge. On an error hint that is a white
   * button with red lettering and a red edge.
   *
   * MUI leaves an outlined button's fill transparent, so the button's surface
   * *is* the alert. This theme's `outlined` slot then gives it `grey[400]`,
   * which measures 1.04-1.27:1 on the five soft fills in both schemes: the
   * control was the tint, and `Choose a plan` on the plan page had no edge at
   * all. A control that cannot be seen is worse than one that is merely
   * quiet: an operator reads it as a sentence.
   *
   * `controlEdge` is the role for exactly this job and is the wrong value
   * here. It was authored against the neutral surfaces (3.72:1 on `surface`)
   * and measures 2.85-2.93 on the error, attention and info tints, so it does
   * not clear the 3:1 a boundary owes. The tone's own ink does, by
   * construction: the rendered `alert-tone` rule already holds the copy drawn
   * in that ink to 4.5:1 on this exact fill, so an edge in it cannot be
   * invisible without the sentence above it being invisible first.
   *
   * **Every button on the tint, not only the action slot's.** This rule was
   * scoped to `.MuiAlert-action`, and the console's most urgent hint put its
   * remedy in the message instead: `Check again`, under a car Beast cannot
   * command, sits in the body of the error alert after the pairing link it
   * belongs with, so it took the `outlined` slot's grey hairline and read as
   * a sentence on the pink. A hint whose control follows something in the
   * copy is a layout the action slot cannot hold, so the rule follows the
   * button wherever it is inside the alert. A contained button included: a
   * second fill on a tint is a second tone in one hint.
   *
   * **The fill is the card's, at rest.** An edge alone on a tint is the tint
   * with a line round it. The paper lifts the control off the hint the way
   * the card lifts the hint off the page, so it reads as something to press
   * before the edge is even resolved. The hover is the one every surface in
   * the console answers with, `theme.hover.surface`, because the button is
   * now a surface.
   *
   * Longhands rather than the `border` shorthand, and the edge is declared at
   * rest as well as on hover, so the hover moves paint and never the box. A
   * disabled one keeps the fill and the edge, so it stays a control in the
   * hint; only the label greys, which is what says it is waiting. A blocked
   * one (`useBlockedAction`, `aria-disabled`) is the same control waiting
   * for the same reason, and greys alike and does not answer the pointer:
   * this rule outranks the blocked look the hook spreads, so without it a
   * blocked Add at the car on a hint read as one to press.
   *
   * **The fills are parameters and not constants, because the ground is
   * not.** On a `filled` alert the ink is the one authored to read on a
   * saturated fill (white for info in light, near-black for the rest in dark)
   * and against paper that measures 1.00:1 and 1.11:1: a paper button there
   * is a blank box. So a filled alert keeps a transparent rest and the fill's
   * own hover step. The slot's own comment says it is stated precisely
   * because nobody looks at it, and it would have shipped wrong for exactly
   * that reason.
   */
  const remedyOnTint = (ink: string, restFill: string, hoverFill: string) => ({
    '& .MuiButton-root': {
      'color': ink,
      'borderColor': ink,
      'borderStyle': 'solid',
      'borderWidth': 1,
      'backgroundColor': restFill,
      '&:hover': {
        backgroundColor: hoverFill,
        borderColor: ink,
      },
      '&.Mui-disabled, &[aria-disabled="true"]': {
        color: theme.palette.action.disabled,
        borderColor: ink,
        backgroundColor: restFill,
      },
      '&[aria-disabled="true"]:hover': {
        backgroundColor: restFill,
        borderColor: ink,
      },
    },
  })

  const standardStyle = (tone: AlertTone) => ({
    'color': roles(tone).text,
    'backgroundColor': roles(tone).soft,
    '& .MuiAlert-icon': {
      // The mark takes the ink of the copy it introduces, not a third value.
      // At `fill` it was the one saturated thing on a soft ground, and on the
      // plan page's notes it fell under 3:1 against its own fill.
      color: roles(tone).text,
    },
    // Off the soft tint and onto the card: the ink is already 4.5:1 on both.
    ...remedyOnTint(
      roles(tone).text,
      theme.palette.background.paper,
      theme.hover.surface,
    ),
  })

  // States its own fill and its own ink. Left to MUI it takes
  // `palette[tone].main | .dark` and `getContrastText`, which is the
  // asymmetric legacy mapping this file exists to stop reading: in dark that
  // put white on `accentDark` at 3.44:1 for info.
  //
  // Which ink reads on which fill is a design fact rather than a theme detail,
  // so it comes from `TONE_INK`: the five fills do not agree, and the first
  // draft of this override picked one slot for all of them and failed the test
  // below on three of the five before it ever rendered.
  const filledStyle = (tone: AlertTone) => ({
    'color': colorTokens[theme.palette.mode][TONE_INK[tone]],
    'backgroundColor': roles(tone).fill,
    '& .MuiAlert-icon': {
      color: colorTokens[theme.palette.mode][TONE_INK[tone]],
    },
    // The ground here is the tone's `fill`, not its `soft`, so both the edge
    // and the hover take values authored against a fill: the ink that reads on
    // one, and the fill's own hover step. No screen draws a filled alert
    // today, and that is precisely why it is stated: `perTone` generates all
    // five tones for all three variants, and the slot nobody looks at is the
    // one that ships wrong.
    ...remedyOnTint(
      colorTokens[theme.palette.mode][TONE_INK[tone]],
      'transparent',
      semanticTokens[theme.palette.mode][tone].fillHover,
    ),
  })

  const outlinedStyle = (tone: AlertTone) => ({
    'color': roles(tone).text,
    'border': `solid 1px ${roles(tone).fill}`,
    'backgroundColor': roles(tone).soft,
    '& .MuiAlert-icon': {
      color: roles(tone).text,
    },
    ...remedyOnTint(
      roles(tone).text,
      theme.palette.background.paper,
      theme.hover.surface,
    ),
  })

  /** `{ standardInfo: …, standardAttention: …, … }` for one variant. */
  const perTone = (
    variant: 'standard' | 'filled' | 'outlined',
    style: (tone: AlertTone) => object,
  ) =>
    Object.fromEntries(
      ALERT_TONES.map((tone) => [
        `${variant}${tone[0].toUpperCase()}${tone.slice(1)}`,
        style(tone),
      ]),
    )

  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: Object.fromEntries(
          ALERT_TONES.map((tone) => [
            tone,
            <Iconify key={tone} size="toolbar" icon={toneGlyph(tone)} />,
          ]),
        ),
      },

      styleOverrides: {
        root: {
          'alignItems': 'flex-start',
          'borderRadius': theme.radii.control,
          // The ground the dismiss is positioned against. Without it the
          // corner resolves to the nearest positioned ancestor, which on the
          // vehicle page is the command panel and on a list page is the
          // viewport.
          'position': 'relative',
          // One inset, on every side, off the space scale. MUI's 6 / 16 is
          // two insets, and the vertical one was then undone by the icon's
          // own block padding below the copy.
          'padding': theme.spacing(1.5),
          // **Without this the action slot's `flex-basis: 100%` is inert.**
          //
          // MUI's alert root is a non-wrapping flex row. The action below
          // declares a full-width basis and has done since it was written,
          // and every word of the comment there about folding onto its own
          // line under the copy described something that never rendered: a
          // greedy item on a nowrap row does not fold, it shrinks against the
          // message beside it. So `Choose a plan` sat mid-row, squeezed
          // between a sentence and the right edge, with a left padding meant
          // to align it to the copy pushing it further from both.
          'flexWrap': 'wrap',
          // The mark is centred on the line it introduces, and a title is a
          // taller line than the copy under it. Stated on the root because it
          // is a fact about what the alert contains: the icon slot cannot see
          // whether there is a title beside it.
          '&:has(.MuiAlertTitle-root) .MuiAlert-icon': {
            height: TITLE_LINE,
          },

          // **The dismiss is in the corner, and the corner is here.**
          //
          // MUI renders `action` instead of its own close button when a call
          // site gives both, so the one alert in the console that can be
          // dismissed put its × into the action row to make it appear, and
          // drew `Retry ×` side by side along the bottom edge, the giving-up
          // control adjacent to the retrying one. Every other dismissible
          // surface in this product puts it top right.
          //
          // The offset centres the mark on the line it stands beside, the way
          // the alert's own glyph is centred on the other end of that line: a
          // 32px box against a 20px line box overhangs 6px either side, so the
          // top is the root's inset less that overhang. With a title the line
          // is the title's, for the reason the icon slot's height is.
          [`& .${HINT_DISMISS_CLASS}`]: {
            position: 'absolute',
            top: `calc(${theme.spacing(1.5)} - ${DISMISS_OVERHANG}px)`,
            right: theme.spacing(1.5),
          },
          [`&:has(.MuiAlertTitle-root) .${HINT_DISMISS_CLASS}`]: {
            top: `calc(${theme.spacing(1.5)} - ${DISMISS_OVERHANG_TITLE}px)`,
          },
          // The room it stands in. An absolutely positioned control is out of
          // flow, so without this the copy runs under it, and the copy here
          // is often the longest thing on the screen, a car's refusal quoted
          // back verbatim.
          [`&:has(.${HINT_DISMISS_CLASS})`]: {
            paddingRight: `calc(${theme.spacing(1.5)} + ${DISMISS_BOX}px)`,
          },
        },
        icon: {
          // The mark is centred on the copy's first line: a box exactly one
          // line tall with the glyph centred inside it. Whichever of the two
          // is taller overhangs the other equally, so the two centres are the
          // same line whatever the icon step and whatever the type scale say.
          //
          // MUI's `7px 0` is what has to go, both halves of it. Zeroing only
          // the top left the bottom pushing the root 7px taller than its copy,
          // which is where the 16px under a one-line hint came from.
          padding: 0,
          height: COPY_LINE,
          alignItems: 'center',
          // Stated rather than left to MUI's 12px, because the action row
          // below now aligns itself to the copy by adding this same gap to the
          // glyph's width. A default that moved would move one of the two.
          marginRight: theme.spacing(1.5),
        },
        message: {
          // **Not `width: 100%`.** That is a hypothetical size of the whole
          // line, and on a root that wraps it folds the copy under the mark,
          // which is what every hint in the console drew. The basis is zero
          // and not `auto`: at `auto` the copy claims its own content width
          // and folds again as soon as the column is narrower than the
          // sentence. `minWidth: 0` lets it shrink past an unbreakable token,
          // and the copy in this console is full of addresses.
          'flexGrow': 1,
          'flexBasis': 0,
          'minWidth': 0,
          'paddingBlock': 0,
          '& .MuiAlertTitle-root': {
            // MUI's optical nudge, gone for the reason the icon's was: it
            // measures one rendering, and it lifts the title off the line the
            // mark is centred against.
            marginTop: 0,
            marginBottom: theme.spacing(0.5),
          },
        },
        action: {
          // **The remedy is a row of its own, under the sentence, starting
          // where the sentence starts.**
          //
          // This slot has argued both sides and shipped neither. It first put
          // the action on the copy's left edge, which never rendered: the root
          // did not wrap, so the action stayed on the copy's row and the left
          // padding meant to align it pushed it into the middle of one. It was
          // then made trailing, on the argument that the last thing in reading
          // order should be the last thing on the line, and that is what
          // `Choose a plan` drew: a control alone at the far right of a
          // 1400px band, a hand's width from the sentence that is the only
          // reason it exists, with the whole empty middle of the alert between
          // them.
          //
          // Reading order is satisfied by the row being second. What the
          // horizontal position decides is whether the control looks like it
          // belongs to the sentence above it, and at this width nothing at the
          // far right of a full-bleed band does. So it starts on the copy's
          // own left edge: the remedy sits under the first word of the reason
          // for it, and the two read as one block however wide the page gets.
          //
          // The inset is the mark's column (the glyph's box plus the gap MUI
          // puts after it), and it is stated from the same tokens the icon
          // slot is sized from rather than typed, so the two cannot drift.
          'justifyContent': 'flex-start',
          'paddingLeft': `calc(${iconTokens.sizes.toolbar}px + ${theme.spacing(
            1.5,
          )})`,
          // Unless there is no mark to align past. `hideIconVariant` on a
          // toast, and `icon={false}` anywhere, leave the copy on the root's
          // own inset, and an inset measured off a glyph that is not drawn
          // would indent the remedy past nothing. The slot nobody looks at is
          // the one that ships wrong, which is the argument the filled variant
          // two comments up is stated for.
          ':not(:has(.MuiAlert-icon)) > &': {
            paddingLeft: 0,
          },
          'marginLeft': 0,
          'marginRight': 0,
          'paddingTop': theme.spacing(1),
          'width': '100%',
          'flexBasis': '100%',
          'minWidth': 0,
          'display': 'flex',
          'flexWrap': 'wrap',
          'gap': theme.spacing(1),
          'alignItems': 'center',
          '& button:not(:first-of-type)': {
            marginLeft: 0,
          },
          // The row's padding is equal on all four sides, which is 4px less
          // horizontal slack than MUI's default 6/16, and the remedy's label
          // is sometimes an address. `Reconnect
          // agent-preview@electricbeast.local` is one unbreakable token wider
          // than a phone, and it ran 4px past the content area rather than
          // wrapping. A button may be as wide as the row and no wider, and a
          // label with nowhere to break breaks anywhere.
          '& button': {
            maxWidth: '100%',
          },
          '& .MuiButton-root': {
            overflowWrap: 'anywhere',
          },
        },

        ...perTone('standard', standardStyle),
        ...perTone('filled', filledStyle),
        ...perTone('outlined', outlinedStyle),
      },
    },
  }
}
